import { AudienceApiResponse, AudienceFrontendPreview } from '../../../services/audience-api.service';
import { AudienceStatus } from './audience.types';

// Helper puri di normalizzazione/formattazione dei valori che arrivano dall'API audience.
// Estratti da AudienceComponent per poterli testare in isolamento: il comportamento è identico.

export function toNumericValue(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim().length) {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
}

export function normalizeStatus(status?: string): AudienceStatus {
  switch ((status || '').toLowerCase()) {
    case 'active':
      return 'active';
    case 'draft':
    case 'preview':
      return 'draft';
    case 'stopped':
    case 'paused':
      return 'paused';
    case 'error':
      return 'error';
    default:
      return 'pending';
  }
}

export function formatSchedule(value?: string): string {
  if (!value) {
    return '—';
  }
  const lower = value.toLowerCase();
  if (lower === 'daily') return 'Daily';
  if (lower === 'weekly') return 'Weekly';
  if (lower === 'once') return 'Once';
  return value;
}

export function formatDate(value?: string | null): string {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  // Usa UTC per evitare shift di timezone che causano -1 giorno
  return date.toLocaleDateString('it-IT', { timeZone: 'UTC' });
}

export function formatVolume(value: number | string | null | undefined): string {
  if (value === null || value === undefined) {
    return '—';
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return String(value);
  }
  if (numeric >= 1_000_000) {
    return `${(numeric / 1_000_000).toFixed(1)}M`;
  }
  if (numeric >= 1_000) {
    return `${(numeric / 1_000).toFixed(1)}K`;
  }
  return `${numeric}`;
}

export function parseVolume(rawVolume: string): number {
  if (!rawVolume) {
    return 0;
  }
  const normalized = rawVolume.replace(/[, ]/g, '').toLowerCase();
  const isLessThan = normalized.startsWith('<');
  const valuePart = normalized.replace('<', '');
  let factor = 1;
  let numericPart = valuePart;

  if (valuePart.endsWith('m')) {
    factor = 1_000_000;
    numericPart = valuePart.slice(0, -1);
  } else if (valuePart.endsWith('k')) {
    factor = 1_000;
    numericPart = valuePart.slice(0, -1);
  }

  const numericValue = parseFloat(numericPart);
  const base = Number.isNaN(numericValue) ? 0 : numericValue * factor;
  return isLessThan ? base * 0.99 : base;
}

export function normalizeTagsValue(value?: string[] | string | null): string[] | null {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim().length) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [value];
    } catch {
      return [value];
    }
  }
  return null;
}

export function normalizePublicFlag(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value === 1;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true';
  }
  return false;
}

export function normalizeFrontendPreview(raw: AudienceApiResponse['FrontendPreview']): AudienceFrontendPreview | null {
  if (!raw) {
    return null;
  }
  let preview: AudienceFrontendPreview | null = null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      preview = parsed && typeof parsed === 'object' ? parsed as AudienceFrontendPreview : null;
    } catch {
      return null;
    }
  } else {
    preview = typeof raw === 'object' ? raw as AudienceFrontendPreview : null;
  }
  if (preview?.AudienceType === 'old-platform') {
    return { ...preview, AudienceType: null };
  }
  return preview;
}

export function extractCreatorFallback(api: AudienceApiResponse): string {
  const creator =
    api.CreatedByUserName ||
    api.CreatedByName ||
    api.CreatedBy ||
    api.CreatedByUser?.Name ||
    api.CreatedByUserID;

  const normalized = creator?.toString().trim();
  return normalized && normalized.length ? normalized : '—';
}

export function parseChannelData(raw: unknown): Record<string, unknown> | null {
  if (!raw) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? raw as Record<string, unknown> : null;
}

/** Frequenza del form ('Daily'/'Weekly'/...) → valore Schedulation del backend. */
export function mapFrequency(value: string): string {
  const lower = (value || '').toLowerCase();
  if (lower === 'weekly') return 'weekly';
  if (lower === 'monthly') return 'monthly';
  if (lower === 'once') return 'once';
  return 'daily';
}

/** Schedulation formattata (vedi formatSchedule) → opzione del form. */
export function mapScheduleToForm(schedule: string): string {
  const lower = (schedule || '').toLowerCase();
  if (lower.includes('week')) {
    return 'Weekly';
  }
  if (lower.includes('month')) {
    return 'Monthly';
  }
  if (lower.includes('once')) {
    return 'Once';
  }
  return 'Daily';
}

export function normalizeDate(value?: string | null): string | null {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString().slice(0, 10);
}

export function toInputDate(value?: string | null): string {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString().slice(0, 10);
}

export function todayIsoDate(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

export function addDays(dateString: string, days: number): string {
  const date = new Date(dateString);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

export function isForeverEndDate(value?: string | null): boolean {
  if (!value) {
    return true;
  }

  const inputDate = toInputDate(value);
  return !!inputDate && inputDate >= '2050-11-30';
}

export function resolveEndDate(formValue: { ongoing: boolean; endDate: string }): string {
  if (!formValue.ongoing) {
    const provided = normalizeDate(formValue.endDate);
    if (provided) {
      return provided;
    }
  }
  // Per ongoing (Update Forever): end date = 1 dicembre 2050
  return '2050-12-01';
}

export function calculateTimerange(start: string, end: string): number {
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return 30;
  }
  const diff = Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  return diff > 0 ? diff : 30;
}

export function isInCurrentMonth(value?: string | null, now: Date = new Date()): boolean {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  return date.getUTCFullYear() === now.getUTCFullYear()
    && date.getUTCMonth() === now.getUTCMonth();
}
